import {
  HatchPatternConfig,
  HatchPatternType,
} from 'src/components/viewers/SheetsViewerV2/shapes/utils/hatch-patterns';

/**
 * Delimiter used to separate color from pattern in encoded strings.
 * Format: "color::patternJson"
 * Example: "#ff0000::{"type":"diagonal","angle":45,"gap":20}"
 */
const PATTERN_DELIMITER = '::';

/**
 * Encodes a color and optional hatch pattern into a single string.
 * If pattern is null or type is "none", only the color is returned.
 *
 * @param color - The base color (hex or gradient)
 * @param pattern - Optional hatch pattern configuration
 * @returns Encoded string with color and pattern
 */
export function encodeColorWithPattern(
  color: string,
  pattern: HatchPatternConfig | null
): string {
  // If no pattern or pattern type is "none", just return the color
  if (!pattern || pattern.type === HatchPatternType.None) {
    // If color already has a pattern encoded, strip it
    return extractColorFromEncoded(color);
  }

  // Get the base color (strip any existing pattern encoding)
  const baseColor = extractColorFromEncoded(color);

  // Encode the pattern as JSON
  const patternJson = JSON.stringify(pattern);

  return `${baseColor}${PATTERN_DELIMITER}${patternJson}`;
}

/**
 * Decodes an encoded color string into its color and pattern components.
 * Handles backward compatibility - colors without patterns return null pattern.
 *
 * @param encoded - The encoded color string (may or may not contain pattern)
 * @returns Object with color and pattern (pattern may be null)
 */
export function decodeColorWithPattern(encoded: string): {
  color: string;
  pattern: HatchPatternConfig | null;
} {
  if (!encoded) {
    return { color: '#000000', pattern: null };
  }

  const delimiterIndex = encoded.indexOf(PATTERN_DELIMITER);

  // No delimiter means no pattern encoded
  if (delimiterIndex === -1) {
    return { color: encoded, pattern: null };
  }

  const color = encoded.substring(0, delimiterIndex);
  const patternJson = encoded.substring(
    delimiterIndex + PATTERN_DELIMITER.length
  );

  try {
    const pattern = JSON.parse(patternJson) as HatchPatternConfig;
    // Validate that it has the required type field
    if (
      pattern &&
      pattern.type &&
      Object.values(HatchPatternType).includes(pattern.type)
    ) {
      return { color, pattern };
    }
    return { color, pattern: null };
  } catch {
    // Invalid JSON, return just the color
    return { color, pattern: null };
  }
}

/**
 * Extracts just the color portion from an encoded string.
 * Useful when you only need the color and want to ignore the pattern.
 *
 * @param encoded - The encoded color string
 * @returns The color portion only
 */
export function extractColorFromEncoded(encoded: string): string {
  if (!encoded) {
    return '#000000';
  }

  const delimiterIndex = encoded.indexOf(PATTERN_DELIMITER);
  if (delimiterIndex === -1) {
    return encoded;
  }

  return encoded.substring(0, delimiterIndex);
}

/**
 * Extracts just the pattern from an encoded string.
 * Returns null if no pattern is encoded.
 *
 * @param encoded - The encoded color string
 * @returns The pattern configuration or null
 */
export function extractPatternFromEncoded(
  encoded: string
): HatchPatternConfig | null {
  return decodeColorWithPattern(encoded).pattern;
}

/**
 * Checks if an encoded string contains a pattern.
 *
 * @param encoded - The encoded color string
 * @returns True if a pattern is encoded
 */
export function hasEncodedPattern(encoded: string): boolean {
  return encoded?.includes(PATTERN_DELIMITER) ?? false;
}
